/** The version of the snapshot layout the request forms write. Bump it if a form changes how it stores a value. */
export const DRAFT_PAYLOAD_VERSION = 1;

/**
 * What a request form stores in a draft: the form control values, plus the few pieces of
 * component state the controls do not hold (such as the chosen matter number). Documents are not part of it.
 */
export type DraftSnapshot = {
    values: Record<string, unknown>;
    state: Record<string, unknown>;
};

/** A snapshot with nothing in it, used when a saved draft cannot be read. */
export const emptySnapshot = (): DraftSnapshot => ({ values: {}, state: {} });

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * Reads the JSON a draft was saved with. The text comes from the server, so its shape is checked rather than assumed.
 * @param payload The saved JSON text.
 * @returns The snapshot, or null if the text is not a valid snapshot.
 */
export const parseSnapshot = (payload: string): DraftSnapshot | null => {
    try {
        const parsed: unknown = JSON.parse(payload);
        if (!isRecord(parsed) || !isRecord(parsed['values'])) {
            return null;
        }
        return { values: parsed['values'], state: isRecord(parsed['state']) ? parsed['state'] : {} };
    } catch {
        return null;
    }
};

/**
 * Writes a snapshot as the JSON text stored in the draft.
 * @param snapshot The snapshot to save.
 */
export const serializeSnapshot = (snapshot: DraftSnapshot): string => JSON.stringify(snapshot);

/**
 * Reads a text value from a saved snapshot section, tolerating a missing or differently typed entry.
 * @param source The `values` or `state` section.
 * @param key The entry to read.
 */
export const readString = (source: Record<string, unknown>, key: string): string => {
    const value = source[key];
    return typeof value === 'string' ? value : '';
};

/**
 * Reads a yes/no value from a saved snapshot section.
 * @param source The `values` or `state` section.
 * @param key The entry to read.
 */
export const readBoolean = (source: Record<string, unknown>, key: string): boolean => source[key] === true;

/**
 * Reads a number from a saved snapshot section.
 * @param source The `values` or `state` section.
 * @param key The entry to read.
 * @returns The number, or null if the entry is missing or not a number.
 */
export const readNumber = (source: Record<string, unknown>, key: string): number | null => {
    const value = source[key];
    return typeof value === 'number' && Number.isFinite(value) ? value : null;
};

/**
 * Reads a date from a saved snapshot section (dates are saved as ISO text).
 * @param source The `values` or `state` section.
 * @param key The entry to read.
 * @returns The date, or null if the entry is missing or not a valid date.
 */
export const readDate = (source: Record<string, unknown>, key: string): Date | null => {
    const value = source[key];
    if (typeof value !== 'string') return null;
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
};

/**
 * Reads a text-to-text map (such as invoice amounts) from a saved snapshot section.
 * @param source The `values` or `state` section.
 * @param key The entry to read.
 */
export const readStringMap = (source: Record<string, unknown>, key: string): Record<string, string> => {
    const value = source[key];
    const map: Record<string, string> = {};
    if (!isRecord(value)) return map;
    for (const [entryKey, entryValue] of Object.entries(value)) {
        if (typeof entryValue === 'string') {
            map[entryKey] = entryValue;
        }
    }
    return map;
};
